/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ClientType,
  ListAuthClientsRequest,
  ListAuthClientsSecurity,
} from "../models/operations/listauthclients.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AuthClientsInfiniteQueryData,
  AuthClientsPageParams,
  AuthClientsQueryData,
  buildAuthClientsInfiniteQuery,
  buildAuthClientsQuery,
  prefetchAuthClients,
  prefetchAuthClientsInfinite,
  queryKeyAuthClients,
  queryKeyAuthClientsInfinite,
} from "./authClients.core.js";
export {
  type AuthClientsInfiniteQueryData,
  type AuthClientsPageParams,
  type AuthClientsQueryData,
  buildAuthClientsInfiniteQuery,
  buildAuthClientsQuery,
  prefetchAuthClients,
  prefetchAuthClientsInfinite,
  queryKeyAuthClients,
  queryKeyAuthClientsInfinite,
};

export type AuthClientsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAuthClients authClients
 *
 * @remarks
 * List the auth clients in the caller's organization. Requires org:read.
 */
export function useAuthClients(
  request?: ListAuthClientsRequest | undefined,
  security?: ListAuthClientsSecurity | undefined,
  options?: QueryHookOptions<AuthClientsQueryData, AuthClientsQueryError>,
): UseQueryResult<AuthClientsQueryData, AuthClientsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAuthClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAuthClients authClients
 *
 * @remarks
 * List the auth clients in the caller's organization. Requires org:read.
 */
export function useAuthClientsSuspense(
  request?: ListAuthClientsRequest | undefined,
  security?: ListAuthClientsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AuthClientsQueryData,
    AuthClientsQueryError
  >,
): UseSuspenseQueryResult<AuthClientsQueryData, AuthClientsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAuthClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAuthClients authClients
 *
 * @remarks
 * List the auth clients in the caller's organization. Requires org:read.
 */
export function useAuthClientsInfinite(
  request?: ListAuthClientsRequest | undefined,
  security?: ListAuthClientsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    AuthClientsInfiniteQueryData,
    AuthClientsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<AuthClientsInfiniteQueryData, AuthClientsPageParams>,
  AuthClientsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    AuthClientsInfiniteQueryData,
    AuthClientsQueryError,
    InfiniteData<AuthClientsInfiniteQueryData, AuthClientsPageParams>,
    QueryKey,
    AuthClientsPageParams
  >({
    ...buildAuthClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listAuthClients authClients
 *
 * @remarks
 * List the auth clients in the caller's organization. Requires org:read.
 */
export function useAuthClientsInfiniteSuspense(
  request?: ListAuthClientsRequest | undefined,
  security?: ListAuthClientsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    AuthClientsInfiniteQueryData,
    AuthClientsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<AuthClientsInfiniteQueryData, AuthClientsPageParams>,
  AuthClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    AuthClientsInfiniteQueryData,
    AuthClientsQueryError,
    InfiniteData<AuthClientsInfiniteQueryData, AuthClientsPageParams>,
    QueryKey,
    AuthClientsPageParams
  >({
    ...buildAuthClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setAuthClientsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId?: string | undefined;
      clientType?: ClientType | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: AuthClientsQueryData,
): AuthClientsQueryData | undefined {
  const key = queryKeyAuthClients(...queryKeyBase);

  return client.setQueryData<AuthClientsQueryData>(key, data);
}

export function invalidateAuthClients(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId?: string | undefined;
      clientType?: ClientType | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "list", ...queryKeyBase],
  });
}

export function invalidateAllAuthClients(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "list"],
  });
}
